/**
 * useBookingForm Hook
 * Manages booking form state, validation, and submission
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BookingFormData, PackageConfig, Addon, BookingSettings } from "@/types";
import { useCartStore } from "@/store/cartStore";
import { useAuthStore } from "@/store/authStore";
import { checkTimeSlotAvailability } from "@/services/booking.service";
import { validateEmail, validatePhone, validateRequired } from "@/utils/validation";
import { sanitizeString, sanitizeEmail, sanitizePhone } from "@/utils/sanitize";
import { ROUTES } from "@/config/constants";
import { BookingFormErrors, BookingFormTouched, TimeSlotOption, DAY_NAMES } from "./types";

interface UseBookingFormProps {
  settings: BookingSettings | null;
  addons: Addon[];
}

interface UseBookingFormReturn {
  // State
  step: number;
  formData: Partial<BookingFormData>;
  errors: BookingFormErrors;
  touched: BookingFormTouched;
  loading: boolean;
  showConfirmDialog: boolean;
  selectedPackage: PackageConfig | null;
  selectedAddons: Addon[];
  availableTimeSlots: TimeSlotOption[];
  checkingAvailability: boolean;

  // Computed
  subtotalAmount: number;
  totalAmount: number;

  // Actions
  setStep: (step: number) => void;
  setFormData: React.Dispatch<React.SetStateAction<Partial<BookingFormData>>>;
  setErrors: React.Dispatch<React.SetStateAction<BookingFormErrors>>;
  setTouched: React.Dispatch<React.SetStateAction<BookingFormTouched>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPackage: React.Dispatch<React.SetStateAction<PackageConfig | null>>;
  setShowConfirmDialog: (show: boolean) => void;
  handleNext: () => void;
  handleBack: () => void;
  handleSubmit: () => void;
  confirmSubmit: () => Promise<void>;
  handleFieldBlur: (fieldName: string, value: string) => void;
  handlePhoneChange: (value: string) => void;
  handleAddonToggle: (addonId: string) => void;
  formatPhoneNumber: (value: string) => string;
  validateStep: (stepNumber: number) => boolean;
}

export function useBookingForm({ settings, addons }: UseBookingFormProps): UseBookingFormReturn {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const addItem = useCartStore((state) => state.addItem);
  const setBookingData = useCartStore((state) => state.setBookingData);

  // Form state
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<BookingFormData>>({
    addonIds: [],
  });
  const [errors, setErrors] = useState<BookingFormErrors>({});
  const [touched, setTouched] = useState<BookingFormTouched>({});
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<PackageConfig | null>(null);

  // Time slots
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlotOption[]>([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Selected addons
  const selectedAddons = useMemo(() => {
    if (!formData.addonIds?.length) return [];
    return addons.filter((a) => formData.addonIds?.includes(a.id));
  }, [formData.addonIds, addons]);

  // Calculate amounts
  const subtotalAmount = useMemo(() => {
    return (
      (selectedPackage?.price || 0) + selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
    );
  }, [selectedPackage, selectedAddons]);

  const totalAmount = subtotalAmount;

  // Pre-fill user data when entering step 3
  useEffect(() => {
    if (user && step === 3) {
      setFormData((prev) => ({
        ...prev,
        customerName: prev.customerName || user.displayName,
        customerEmail: prev.customerEmail || user.email,
        customerPhone: prev.customerPhone || user.phoneNumber || "",
      }));
    }
  }, [user, step]);

  // Generate time slots when date changes
  const generateTimeSlots = useCallback(async (): Promise<void> => {
    if (!formData.scheduledDate || !settings) return;

    setCheckingAvailability(true);
    const date = new Date(formData.scheduledDate);
    const dayOfWeek = date.getDay();
    const dayName = DAY_NAMES[dayOfWeek] as keyof typeof settings.weeklySchedule;

    const daySchedule = settings.weeklySchedule[dayName];

    if (!daySchedule.enabled) {
      setAvailableTimeSlots([]);
      setCheckingAvailability(false);
      return;
    }

    const slots: TimeSlotOption[] = [];

    for (const timeRange of daySchedule.timeSlots) {
      const startTime = timeRange.startTime;
      const endTime = timeRange.endTime;

      const [startHour, startMin] = startTime.split(":").map(Number);
      const [endHour, endMin] = endTime.split(":").map(Number);

      let currentTime = startHour * 60 + startMin;
      const endTimeMin = endHour * 60 + endMin;

      while (currentTime < endTimeMin) {
        const hour = Math.floor(currentTime / 60);
        const min = currentTime % 60;
        const timeStr = `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;

        const slotId = `${date.toISOString().split("T")[0]}-${timeStr}`;

        // Check availability
        const isAvailable = await checkTimeSlotAvailability(
          date,
          slotId,
          settings.travelBufferMinutes || 30
        );

        if (isAvailable) {
          // Format for display
          const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
          const ampm = hour >= 12 ? "PM" : "AM";
          const displayTime = `${displayHour}:${min.toString().padStart(2, "0")} ${ampm}`;

          slots.push({ id: slotId, time: displayTime });
        }

        currentTime += settings.slotDuration || 60;
      }
    }

    setAvailableTimeSlots(slots);
    setCheckingAvailability(false);
  }, [formData.scheduledDate, settings]);

  useEffect(() => {
    generateTimeSlots();
  }, [generateTimeSlots]);

  // Validation
  const validateField = useCallback((fieldName: string, value: any): string | null => {
    switch (fieldName) {
      case "customerEmail":
        if (!validateEmail(value || "")) return "Please enter a valid email address";
        return null;
      case "customerPhone":
        if (!validatePhone(value || "")) return "Please enter a valid 10-digit phone number";
        return null;
      case "customerName":
        if (!validateRequired(value)) return "Name is required";
        return null;
      case "propertyAddress":
        if (!validateRequired(value)) return "Property address is required";
        return null;
      case "propertyType":
        if (!value) return "Please select a property type";
        return null;
      default:
        return null;
    }
  }, []);

  const handleFieldBlur = useCallback(
    (fieldName: string, value: string) => {
      setTouched((prev) => ({ ...prev, [fieldName]: true }));
      const error = validateField(fieldName, value);
      if (error) {
        setErrors((prev) => ({ ...prev, [fieldName]: error }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [validateField]
  );

  const validateStep = useCallback(
    (stepNumber: number): boolean => {
      const newErrors: BookingFormErrors = {};

      if (stepNumber === 1) {
        if (!formData.serviceId) newErrors.serviceId = "Please select a service";
        if (!formData.packageId) newErrors.packageId = "Please select a package";
      }

      if (stepNumber === 2) {
        if (!formData.scheduledDate) newErrors.scheduledDate = "Please select a date";
        if (!formData.timeSlotId) newErrors.timeSlotId = "Please select a time slot";
      }

      if (stepNumber === 3) {
        if (!validateRequired(formData.customerName)) newErrors.customerName = "Name is required";
        if (!validateEmail(formData.customerEmail || ""))
          newErrors.customerEmail = "Please enter a valid email address";
        if (!validatePhone(formData.customerPhone || ""))
          newErrors.customerPhone = "Please enter a valid 10-digit phone number";
        if (!validateRequired(formData.propertyAddress))
          newErrors.propertyAddress = "Property address is required";
        if (!formData.propertyType) newErrors.propertyType = "Please select a property type";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [formData]
  );

  const handleNext = useCallback(() => {
    if (validateStep(step)) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  }, [step, validateStep]);

  const handleBack = useCallback(() => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  }, [step]);

  const handleSubmit = useCallback(() => {
    if (!validateStep(3)) {
      setTouched({
        customerName: true,
        customerEmail: true,
        customerPhone: true,
        propertyAddress: true,
        propertyType: true,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setShowConfirmDialog(true);
  }, [validateStep]);

  const confirmSubmit = useCallback(async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const sanitizedFormData: BookingFormData = {
        ...formData,
        customerName: sanitizeString(formData.customerName),
        customerEmail: sanitizeEmail(formData.customerEmail),
        customerPhone: sanitizePhone(formData.customerPhone),
        propertyAddress: sanitizeString(formData.propertyAddress),
        propertyType: sanitizeString(formData.propertyType),
        specialInstructions: formData.specialInstructions
          ? sanitizeString(formData.specialInstructions)
          : undefined,
      } as BookingFormData;

      if (selectedPackage) {
        addItem({
          type: "package",
          name: selectedPackage.name,
          details: selectedPackage.details,
          price: selectedPackage.price,
          quantity: 1,
        });
      }

      selectedAddons.forEach((addon) => {
        addItem({
          type: "addon",
          name: addon.title,
          details: addon.description,
          price: addon.price,
          quantity: 1,
        });
      });

      setBookingData(sanitizedFormData);
      navigate(ROUTES.CART);
    } catch (error) {
      console.error("Error creating booking:", error);
      alert(
        "Unable to add booking to cart. This may be due to a network error or the selected time slot is no longer available. Please refresh the page and try again."
      );
    } finally {
      setLoading(false);
    }
  }, [formData, selectedPackage, selectedAddons, addItem, setBookingData, navigate]);

  const handleAddonToggle = useCallback((addonId: string) => {
    setFormData((prev) => {
      const current = prev.addonIds || [];
      const updated = current.includes(addonId)
        ? current.filter((id) => id !== addonId)
        : [...current, addonId];
      return { ...prev, addonIds: updated };
    });
  }, []);

  const formatPhoneNumber = useCallback((value: string): string => {
    const digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length === 0) return "";
    if (digitsOnly.length <= 3) return digitsOnly;
    if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    }
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  }, []);

  const handlePhoneChange = useCallback(
    (value: string) => {
      const formatted = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, customerPhone: formatted }));
    },
    [formatPhoneNumber]
  );

  return {
    // State
    step,
    formData,
    errors,
    touched,
    loading,
    showConfirmDialog,
    selectedPackage,
    selectedAddons,
    availableTimeSlots,
    checkingAvailability,

    // Computed
    subtotalAmount,
    totalAmount,

    // Actions
    setStep,
    setFormData,
    setErrors,
    setTouched,
    setLoading,
    setSelectedPackage,
    setShowConfirmDialog,
    handleNext,
    handleBack,
    handleSubmit,
    confirmSubmit,
    handleFieldBlur,
    handlePhoneChange,
    handleAddonToggle,
    formatPhoneNumber,
    validateStep,
  };
}
